import React, { useState, useEffect, useRef } from 'react';

interface TreeStageData {
  ascii: string;
  name: string;
  lore: string;
}

interface Star {
  id: string;
  x: number;
  y: number;
  isBuy: boolean;
  angle: number;
  speed: number;
}

const HackedTreeGrowth: React.FC = () => {
  const [marketCap, setMarketCap] = useState(0);
  const [treeStage, setTreeStage] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [stars, setStars] = useState<Star[]>([]);
  const socketRef = useRef<WebSocket | null>(null);
  const [isConnecting, setIsConnecting] = useState(true);
  const [transactions, setTransactions] = useState<Array<{
    mint: string;
    signature: string;
    traderPublicKey: string;
    txType: string;
    timestamp: number;
  }>>([]);

  const treeStages: TreeStageData[] = [
    {
      ascii: `
    _____________________
    |     FERTILE       |
    |      SOIL        |
    ---------------------
    `,
      name: "Sacred Ground",
      lore: "The soil contains the essence of Yggdrasil, waiting for the cosmic seed."
    },
    {
      ascii: `
           .
    _____________________
    |    GERMINATING    |
    |      SEED        |
    ---------------------
    `,
      name: "Seed of Creation",
      lore: "The cosmic seed begins its journey, carrying ancient knowledge within."
    },
    {
      ascii: `
           ^
           |
    _____________________
    |    AWAKENING     |
    ---------------------
    `,
      name: "First Awakening",
      lore: "The primordial force stirs, breaking through the veil of existence."
    },
    {
      ascii: `
          (*)
           |
           |
    _______|_____
    `,
      name: "Ethereal Sprout",
      lore: "The first manifestation of divine energy takes form."
    },
    {
      ascii: `
          (**)
           ||
           ||
    _______|_____
    `,
      name: "Cosmic Seedling",
      lore: "Drawing power from both above and below, the seedling channels celestial energy."
    },
    {
      ascii: `
         (*'*)
          \\|/
           |
           |
    _______|_____
    `,
      name: "Astral Growth",
      lore: "The young tree begins to resonate with the cosmic frequencies."
    },
    {
      ascii: `
          (*)
         (*'*)
          \\|/
           |
           |
    _______|_____
    `,
      name: "Mystic Sapling",
      lore: "The first branches reach towards the heavens, seeking ancient wisdom."
    },
    {
      ascii: `
         (*.*)
        (*'''*)
         \\\\|//
           |
           |
    _______|_____
    `,
      name: "Keeper of Secrets",
      lore: "Each branch now holds fragments of universal knowledge."
    },
    {
      ascii: `
          (*)
         (*'*)
        (*'''*)
         \\\\|//
          |||
           |
    _______|_____
    `,
      name: "Ethereal Sentinel",
      lore: "The tree begins to serve as a bridge between realms."
    },
    {
      ascii: `
         (*.*)
        (*'''*)
       (*'''''*)
         \\\\|//
          |||
          |||
    _______|_____
    `,
      name: "Cosmic Mediator",
      lore: "A channel opens between the physical and spiritual planes."
    },
    {
      ascii: `
         (*.*)
        (*'''*)
       (*'''''*)
      (*'''''''*)
         \\\\|//
          |||
          |||
    ________|______
    `,
      name: "Worldly Anchor",
      lore: "The tree now anchors celestial energies to the material realm."
    },
    {
      ascii: `
         (*.*)     
        (*'''*)   
       (*'''''*) 
      (*'''''''*)
     (*'''''''''*)
         \\\\|//
          |||
          |||
    ________|______
    `,
      name: "Divine Conductor",
      lore: "Each branch resonates with the harmony of the spheres."
    },
    {
      ascii: `
     *    (*.*)     *
        (*'''*)  *
       (*'''''*) 
   *  (*'''''''*)
     (*'''''''''*) *
    (*'''''''''''*)
         \\\\||//
          \\||/
           ||
    ________||_____
    `,
      name: "Celestial Guardian",
      lore: "The tree begins to manifest its own consciousness and purpose."
    },
    {
      ascii: `
     *    (*.*)     *
     *  (*'''*)  *
   *   (*'''''*) *
   * (*'''''''*) *
  *  (*'''''''''*) *
    (*'''''''''''*)
   *    \\\\||//    *
    *    \\||/    
     *    ||    *
    ________||_____
    `,
      name: "Astral Nexus",
      lore: "A gathering point for cosmic energies and celestial beings."
    },
    {
      ascii: `
   * *    (*.*)     * *
  *  *  (*'''*)  *  *
 *  *  (*'''''*) *   *
 * * (*'''''''*) * *
* *  (*'''''''''*) *  *
 * *(*'''''''''''*)* *
 * *    \\\\||//    * *
  * *    \\||/    * *
   * *    ||    * *
    * *   ||   * *
    ________||_____
    `,
      name: "Sacred Axis",
      lore: "The tree now spans all realms, connecting the multiverse."
    }
  ];

  const getTreeStageIndex = (cap: number) => {
    if (cap <= 29) return 0;
    if (cap >= 411) return treeStages.length - 1;

    const thresholds = [
      29, 50, 75, 100, 150,
      200, 250, 275, 300, 325,
      350, 365, 380, 390, 400,
      411
    ];

    for (let i = 0; i < thresholds.length - 1; i++) {
      if (cap >= thresholds[i] && cap < thresholds[i + 1]) {
        return i;
      }
    }
    return treeStages.length - 1;
  };

  const getGrowthStatus = (cap: number) => {
    if (cap <= 29) return 'DORMANT';
    if (cap < 75) return 'AWAKENING';
    if (cap < 100) return 'CHANNELING';
    if (cap < 150) return 'ASCENDING';
    if (cap < 200) return 'ENLIGHTENING';
    if (cap < 250) return 'TRANSCENDING';
    if (cap < 300) return 'HARMONIZING';
    if (cap < 350) return 'ILLUMINATING';
    if (cap < 380) return 'RADIATING';
    if (cap < 390) return 'SANCTIFYING';
    if (cap < 400) return 'TRANSCENDENT';
    return 'DIVINE';
  };

  const addStar = (isBuy: boolean, signature: string) => {
    const startY = isBuy ? -10 : 50;
    const startX = isBuy ? Math.random() * 100 : 50;

    const newStar: Star = {
      id: signature, // Use signature instead of nextStarId
      x: startX,
      y: startY,
      isBuy,
      angle: isBuy ? (Math.random() * 60 - 30) : (Math.random() * 360),
      speed: 0.5 + Math.random() * 0.5
    };

    setStars(prev => [...prev, newStar]);
  };
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    // Only create a new socket if one doesn't exist
    if (!socketRef.current) {
      socketRef.current = new WebSocket('wss://pumpportal.fun/api/data');

      socketRef.current.onopen = () => {
        setIsConnecting(false);
        const payload = {
          method: "subscribeTokenTrade",
          keys: ["Bdr1uZQhxpYigDsDEuwzZamC6XvKvHBdmDPpku48pump"]
        };
        socketRef.current?.send(JSON.stringify(payload));
      };

      socketRef.current.onclose = () => {
        setIsConnecting(true);
      };

      socketRef.current.onmessage = (event: MessageEvent) => {
        try {
          const parsedData = JSON.parse(event.data);
          if (parsedData.marketCapSol) {
            console.log(parsedData);
            setMarketCap(parsedData.marketCapSol);
          }
          if (parsedData.txType === "buy" || parsedData.txType === "sell") {
            addStar(parsedData.txType === "buy", parsedData.signature);

            setTransactions(prevTransactions => {
              const newTransaction = {
                mint: parsedData.mint,
                signature: parsedData.signature,
                traderPublicKey: parsedData.traderPublicKey,
                txType: parsedData.txType,
                timestamp: Date.now()
              };
              return [newTransaction, ...prevTransactions].slice(0, 10);
            });
          }
        } catch (error) {
          console.error('Error parsing WebSocket data:', error);
        }
      };

      socketRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    }

    // Animate stars
    const starInterval = setInterval(() => {
      setStars(prevStars => {
        return prevStars
          .map(star => {
            const newY = star.y + star.speed;
            const newX = star.x + star.speed * Math.tan(star.angle * (Math.PI / 180));
            return { ...star, x: newX, y: newY };
          })
          .filter(star =>
            star.x >= -20 &&
            star.x <= 120 &&
            star.y >= -20 &&
            star.y <= 120
          );
      });
    }, 50);

    return () => {
      clearInterval(cursorInterval);
      clearInterval(starInterval);
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, []); // Empty dependency array

  useEffect(() => {
    setTreeStage(getTreeStageIndex(marketCap));
  }, [marketCap]);

  return (
    <div style={{
      backgroundColor: '#001a1a',
      minHeight: '100vh',
      fontFamily: 'monospace',
      color: '#0f0',
      padding: '20px',
      position: 'relative',
      overflow: 'hidden'
    }}>



      {isConnecting && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(0, 15, 15, 0.95)',
          padding: '20px',
          border: '1px solid #0f0',
          boxShadow: '0 0 10px #0f0',
          zIndex: 1000,
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '10px' }}>ESTABLISHING CONNECTION</div>
          <pre style={{ color: '#0f0' }}>
            {`
[█████     ] 
CONNECTING TO ETHEREAL NETWORK...
`}
          </pre>
        </div>
      )}

      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
        {stars.map(star => (
          <div
            key={star.id}
            style={{
              position: 'absolute',
              left: `${star.x}%`,
              top: `${star.y}%`,
              content: '"*"',
              fontSize: '20px',
              color: star.isBuy ? '#0f0' : '#f00',
              textShadow: `0 0 4px ${star.isBuy ? '#0f0' : '#f00'}`,
              transition: 'all 0.05s linear',
              zIndex: 10
            }}
          >
            *
          </div>
        ))}
      </div>

      <pre style={{
        color: '#0f0',
        textAlign: 'center',
        marginBottom: '20px',
        textShadow: '0 0 5px #0f0',
        animation: 'glitch 1s infinite'
      }}>
        {`
██╗   ██╗ ██████╗  ██████  ██████╗  ██████╗  █████╗ ███████╗██╗██╗     
╚██╗ ██╔╝██╔════╝ ██╔════╝ ██╔══██  ██╔══██╗██╔══██╗██╔════╝██║██║     
╚████╔╝ ██║  ███╗██║  ███╗ ██    ██ ██████╔╝███████║███████╗██║██║     
╚██╔╝  ██║   ██║██║   ██║ ██    ██╗ ██╔══██╗██╔══██║╚════██║██║██║     
 ██║   ╚██████╔╝╚██████╔╝ ███████║  ██║  ██║██║  ██║███████║██║███████╗
 ╚═╝    ╚═════╝  ╚═════╝  ╚═╝  ╚═╝  ╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝╚═╝╚══════╝
                  THE TREE OF COSMIC KNOWLEDGE
`}
      </pre>

      <div style={{
        marginBottom: '20px',
        borderBottom: '1px solid #0f0',
        paddingBottom: '10px'
      }}>
        <span style={{ color: '#0f0' }}>root@COSMIC_TREE:~$ monitor_ethereal_growth.exe</span>
        <br />
        <span style={{ color: '#0f0' }}>root@COSMIC_TREE:~$ ca : Bdr1uZQhxpYigDsDEuwzZamC6XvKvHBdmDPpku48pump </span>
        {showCursor && <span className="cursor">█</span>}
      </div>

      <div style={{
        marginBottom: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px',
        border: '1px solid #0f0',
        boxShadow: '0 0 10px #0f0'
      }}>
        <span>[ETHEREAL_POWER]: {marketCap} SOL</span>
        <span style={{
          color: marketCap > 390 ? '#f0f' : '#0f0',
          textShadow: `0 0 5px ${marketCap > 390 ? '#f0f' : '#0f0'}`
        }}>
        </span>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        borderRadius: '5px',
        border: '1px solid #0f0',
        boxShadow: '0 0 10px #0f0',
        margin: '20px 0',
        backgroundColor: 'rgba(0, 15, 15, 0.9)'
      }}>
        <pre style={{
          color: '#0f0',
          textShadow: '0 0 5px #0f0'
        }}>
          {treeStages[treeStage].ascii}
        </pre>

        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          padding: '10px',
          border: '1px solid #0f0',
          backgroundColor: 'rgba(0, 15, 15, 0.9)',
          width: '100%'
        }}>
          <h3 style={{
            color: '#0ff',
            marginBottom: '10px',
            textShadow: '0 0 5px #0ff'
          }}>
            {treeStages[treeStage].name}
          </h3>
          <p style={{
            color: '#7ff',
            fontStyle: 'italic',
            textShadow: '0 0 3px #7ff'
          }}>
            {treeStages[treeStage].lore}
          </p>
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px',
        border: '1px solid #0f0',
        marginTop: '20px',
        boxShadow: '0 0 10px #0f0'
      }}>
        <span>[ETHEREAL_STATE]: {getGrowthStatus(marketCap)}</span>
      </div>

      {/* New Transaction Table */}
      <div style={{
        marginTop: '20px',
        border: '1px solid #0f0',
        padding: '10px',
        boxShadow: '0 0 10px #0f0'
      }}>
        <div style={{
          borderBottom: '1px solid #0f0',
          paddingBottom: '10px',
          marginBottom: '10px'
        }}>
          [RECENT_TRANSACTIONS]
        </div>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9em'
        }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #0f0' }}>
              <th style={{ padding: '8px', textAlign: 'left' }}>Type</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>CA</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Signature</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Signer</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx, index) => (
              <tr key={tx.signature} style={{
                borderBottom: '1px solid rgba(0, 255, 0, 0.2)'
              }}>
                <td style={{
                  padding: '8px',
                  color: tx.txType === 'buy' ? '#0f0' : '#f00'
                }}>
                  {tx.txType.toUpperCase()}
                </td>
                <td style={{ padding: '8px' }}>
                  {tx.mint.slice(0, 4)}...{tx.mint.slice(-4)}
                </td>
                <td style={{ padding: '8px' }}>
                  <a
                    href={`https://solscan.io/tx/${tx.signature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#0ff',
                      textDecoration: 'none'
                    }}
                  >
                    {tx.signature.slice(0, 4)}...{tx.signature.slice(-4)}
                  </a>
                </td>
                <td style={{ padding: '8px' }}>
                  {tx.traderPublicKey.slice(0, 4)}...{tx.traderPublicKey.slice(-4)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        background: 'radial-gradient(circle at 50% 50%, transparent 0%, rgba(0, 15, 15, 0.97) 100%)',
        zIndex: 1
      }} />

      <style>
        {`
        @keyframes glitch {
          0% {
            text-shadow: 0.05em 0 0 #00fffc, -0.03em -0.04em 0 #fc00ff,
                        0.025em 0.04em 0 #fffc00;
          }
          15% {
            text-shadow: 0.05em 0 0 #00fffc, -0.03em -0.04em 0 #fc00ff,
                        0.025em 0.04em 0 #fffc00;
          }
          16% {
            text-shadow: -0.05em -0.025em 0 #00fffc, 0.025em 0.035em 0 #fc00ff,
                        -0.05em -0.05em 0 #fffc00;
          }
          49% {
            text-shadow: -0.05em -0.025em 0 #00fffc, 0.025em 0.035em 0 #fc00ff,
                        -0.05em -0.05em 0 #fffc00;
          }
          50% {
            text-shadow: 0.05em 0.035em 0 #00fffc, 0.03em 0 0 #fc00ff,
                        0 -0.04em 0 #fffc00;
          }
          99% {
            text-shadow: 0.05em 0.035em 0 #00fffc, 0.03em 0 0 #fc00ff,
                        0 -0.04em 0 #fffc00;
          }
          100% {
            text-shadow: -0.05em 0 0 #00fffc, -0.025em -0.04em 0 #fc00ff,
                        -0.04em -0.025em 0 #fffc00;
          }
        }

        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
          100% {
            transform: translateY(0px);
          }
        }

        .cursor {
          animation: blink 1s step-end infinite;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        `}
      </style>
    </div>
  );
};

export default HackedTreeGrowth;